/**
 * WindowCommandBus — Singleton event bus for cross-window communication.
 *
 * Windows register command handlers on mount and unregister on unmount.
 * CIW (or any sender) dispatches commands; the bus routes them to the
 * correct handler based on the target window type and optional tab ID.
 */

export interface WindowCommandPayload {
  /** Target window type (e.g. 'girasoles_view', 'stl_in', 'stl_out') */
  target: string
  /** Optional specific tab ID when multiple instances exist */
  tabId?: string
  /** Command name (e.g. 'save-points', 'load-points', 'list-points') */
  command: string
  /** Arbitrary arguments for the command */
  args?: Record<string, unknown>
}

export interface WindowCommandResult {
  success: boolean
  data?: unknown
  error?: string
}

type CommandHandler = (
  command: string,
  args?: Record<string, unknown>
) => WindowCommandResult | Promise<WindowCommandResult>

interface RegisteredHandler {
  windowType: string
  tabId: string
  label: string
  handler: CommandHandler
}

type BusListener = (event: {
  type: 'register' | 'unregister'
  windowType: string
  tabId: string
  label: string
}) => void

class WindowCommandBus {
  private handlers = new Map<string, RegisteredHandler>()
  private listeners: BusListener[] = []

  /**
   * Register a window's command handler.
   * @returns An unregister function to call on unmount.
   */
  register(
    windowType: string,
    tabId: string,
    label: string,
    handler: CommandHandler
  ): () => void {
    this.handlers.set(tabId, { windowType, tabId, label, handler })
    this.notifyListeners({ type: 'register', windowType, tabId, label })
    return () => {
      this.handlers.delete(tabId)
      this.notifyListeners({ type: 'unregister', windowType, tabId, label })
    }
  }

  /**
   * Dispatch a command to a specific tab or the first handler matching the window type.
   */
  async dispatch(payload: WindowCommandPayload): Promise<WindowCommandResult> {
    const { target, tabId, command, args } = payload

    // If a specific tabId is given, try that first
    if (tabId) {
      const entry = this.handlers.get(tabId)
      if (entry) {
        return entry.handler(command, args)
      }
      return { success: false, error: `No handler registered for tab ${tabId}` }
    }

    // Otherwise find first handler matching the window type
    for (const entry of this.handlers.values()) {
      if (entry.windowType === target) {
        return entry.handler(command, args)
      }
    }

    return { success: false, error: `No ${target} window is currently open` }
  }

  /**
   * List all registered windows.
   */
  listWindows(): Array<{ windowType: string; tabId: string; label: string }> {
    return Array.from(this.handlers.values()).map(({ windowType, tabId, label }) => ({
      windowType,
      tabId,
      label,
    }))
  }

  /**
   * Subscribe to registration changes.
   * @returns An unsubscribe function.
   */
  addListener(listener: BusListener): () => void {
    this.listeners.push(listener)
    return () => {
      this.listeners = this.listeners.filter(l => l !== listener)
    }
  }

  private notifyListeners(event: Parameters<BusListener>[0]) {
    for (const l of this.listeners) {
      try {
        l(event)
      } catch {
        // Listener errors are non-fatal
      }
    }
  }
}

export const windowCommandBus = new WindowCommandBus()
